import * as React from "react"
import _ from "lodash"
import styled from "styled-components"
import PrimaryButton from "../../components/atoms/buttons/PrimaryButton"
import LoadingSpinner from "../../components/atoms/LoadingSpinner"
import { renderHtmlToPdfBlobUri } from "../reports-helpers"
import Report, { A4 } from "./Report"
import { ReportData, ReportOptions } from ".."
import { useDataPlanReport } from "../hooks/useDataPlanReport"
import { useGroupReport } from "../hooks/useGroupReport"
import { useTerminalReport } from "../hooks/useTerminalReport"
import Subtitle from "../../components/atoms/typography/Subtitle"

type Props = {
  options: ReportOptions
}

const LoadingContainer = styled(A4)`
  display: grid;
  place-items: center;
  background: none;
`

const ReportContainer = styled.div`
  overflow: auto;
  width: min-content;
  padding: ${(props) => props.theme.spacings.larger};
  background-color: ${(props) => props.theme.colors.greyLight};
  scrollbar-gutter: stable both-edges;
  margin-block: ${({ theme }) => theme.spacings.large};
`

const ModalContent = styled.div`
  margin-inline: auto;
  width: min-content;
  display: flex;
  flex-direction: column;
  align-items: start;
  max-height: 75vh;
`

const useReportData = (options: ReportOptions): { data: ReportData | undefined; loading: boolean } => {
  switch (options.type) {
    default:
    case "dataplan":
      return useDataPlanReport(options)
    case "group":
      return useGroupReport(options)
    case "terminal":
      return useTerminalReport(options)
  }
}

/**
 * Fetches data and renders the report, as well as showing a preview.
 */
const ReportPreview: React.FC<Props> = ({ options }) => {
  const ref = React.useRef<HTMLDivElement>(null)
  const [result, setResult] = React.useState<string>()
  const [isGenerating, setIsGenerating] = React.useState(false)

  const { data, loading } = useReportData(options)

  const generateReport = async () => {
    if (!loading) {
      const result = renderHtmlToPdfBlobUri(ref.current!)
      setIsGenerating(true)
      setResult(await result)
      setIsGenerating(false)
      return result
    }
  }

  const openPdf = (url: string = result!) => {
    window.open(url, "_blank")
  }

  const onClickOpen = async () => {
    if (!result) {
      openPdf(await generateReport())
    } else {
      openPdf()
    }
  }

  return (
    <div>
      <ModalContent>
        <Subtitle>Report preview</Subtitle>
        <ReportContainer>
          {loading ? (
            <LoadingContainer>
              <LoadingSpinner />
            </LoadingContainer>
          ) : (
            <Report options={options} data={data!} ref={ref} />
          )}
        </ReportContainer>
        <PrimaryButton
          disabled={!data || isGenerating}
          title={isGenerating ? "Loading.." : "Export"}
          onClick={onClickOpen}
        />
      </ModalContent>
    </div>
  )
}

export default ReportPreview
